/**
 * AppleCallback — this page runs INSIDE the popup opened by
 * `useAppleSignIn().signIn()` after Apple redirects the popup to
 * https://<origin>/auth/apple/callback?code=…&state=…
 *
 * Its only job:
 *   1. Parse `code`, `state`, `user`, `error` from the URL (Apple can
 *      put params in the query string when response_mode=query, or in
 *      the URL fragment as a fallback).
 *   2. postMessage them back to `window.opener` so the opener's
 *      `signIn()` promise can resolve.
 *   3. Close itself.
 *
 * If the user opens this URL directly (no `window.opener`), we render
 * a small hint and offer a link back to /login.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Seo } from "@/lib/useSeo";

const POPUP_MESSAGE_TYPE = "brontoy-apple-callback";
const BROADCAST_CHANNEL_NAME = "brontoy-apple-auth";
const APPLE_RESULT_KEY = "brontoy_apple_result";

/** Read a param from BOTH `?query` and `#fragment` so we handle any
 *  future Apple response_mode change without breaking. */
function readAllParams(searchParams) {
  const out = {};
  for (const [k, v] of searchParams.entries()) out[k] = v;
  // Fragment support (response_mode=fragment)
  const frag = window.location.hash.replace(/^#/, "");
  if (frag) {
    for (const [k, v] of new URLSearchParams(frag).entries()) {
      if (!(k in out)) out[k] = v;
    }
  }
  return out;
}

export default function AppleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("Completing sign-in with Apple…");
  const [inPopup, setInPopup] = useState(true);

  useEffect(() => {
    const params = readAllParams(searchParams);
    // `user` arrives as URL-encoded JSON when Apple returns it.
    let userObj = null;
    if (params.user) {
      try {
        const parsed = JSON.parse(params.user);
        userObj = {
          email: parsed?.email || null,
          firstName: parsed?.name?.firstName || null,
          lastName: parsed?.name?.lastName || null,
        };
      } catch {
        userObj = null;
      }
    }

    const payload = {
      type: POPUP_MESSAGE_TYPE,
      code: params.code || null,
      state: params.state || null,
      user: userObj,
      error: params.error || null,
      error_description: params.error_description || null,
    };

    console.log("[BrontoY Apple/callback] received params:", {
      code_present: !!payload.code,
      code_length: payload.code?.length || 0,
      state_present: !!payload.state,
      user_present: !!payload.user,
      error: payload.error,
      has_window_opener: !!window.opener,
    });

    // Broadcast the payload on THREE channels for redundancy, ordered
    // by reliability under Cross-Origin-Opener-Policy (COOP):
    //   1. localStorage `storage` event — PRIMARY.  Fires in every
    //      same-origin window regardless of browsing-context group.
    //      This is the only channel that reliably works when Apple's
    //      authorize page has severed the popup ↔ opener link.
    //   2. BroadcastChannel — works when opener and popup share a
    //      browsing-context group (i.e., COOP didn't split them).
    //   3. window.opener.postMessage — legacy, works only when
    //      `window.opener` survived the cross-origin round-trip.
    //
    // The opener listens on all three and resolves on whichever
    // arrives first.  Multiple deliveries are idempotent (opener
    // sets `done=true` after the first).
    let storageSent = false;
    try {
      // Timestamp inside the payload avoids stale-value collisions on
      // rapid retries — the `storage` event only fires if newValue
      // differs from oldValue, so appending a timestamp guarantees
      // uniqueness across attempts.
      const enriched = { ...payload, _ts: Date.now() };
      localStorage.setItem(APPLE_RESULT_KEY, JSON.stringify(enriched));
      storageSent = true;
      console.log("[BrontoY Apple/callback] payload written to localStorage");
      // Clean up the key after a short window so it can't be
      // resurrected by a future signIn attempt.  30s is well beyond
      // any realistic opener-side processing time.
      setTimeout(() => {
        try { localStorage.removeItem(APPLE_RESULT_KEY); } catch { /* ignore */ }
      }, 30_000);
    } catch (e) {
      console.warn("[BrontoY Apple/callback] localStorage unavailable:", e);
    }

    let broadcastSent = false;
    try {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.postMessage(payload);
      broadcastSent = true;
      setTimeout(() => {
        try { channel.close(); } catch { /* ignore */ }
      }, 200);
      console.log("[BrontoY Apple/callback] payload sent via BroadcastChannel");
    } catch (e) {
      console.warn("[BrontoY Apple/callback] BroadcastChannel unavailable:", e);
    }

    const opener = window.opener && !window.opener.closed ? window.opener : null;
    let openerMsgSent = false;
    if (opener) {
      try {
        opener.postMessage(payload, window.location.origin);
        openerMsgSent = true;
        console.log("[BrontoY Apple/callback] payload sent via window.opener.postMessage");
      } catch (e) {
        console.warn("[BrontoY Apple/callback] postMessage failed:", e);
      }
    } else {
      console.log("[BrontoY Apple/callback] window.opener is null (COOP severed the link) — relying on localStorage + BroadcastChannel");
    }

    // If ALL three paths failed and the user opened this URL directly,
    // fall through to the visual "return to sign-in tab" fallback.
    if (!storageSent && !broadcastSent && !openerMsgSent) {
      setInPopup(false);
      if (payload.error) {
        setStatus("Sign in with Apple was cancelled or failed.");
      } else if (payload.code) {
        setStatus(
          "Sign in received but this window can't complete it here. Please return to the sign-in tab."
        );
      } else {
        setStatus("No sign-in in progress. Redirecting…");
        const t = setTimeout(() => navigate("/login", { replace: true }), 1500);
        return () => clearTimeout(t);
      }
      return;
    }

    setStatus(
      payload.error
        ? "Sign in with Apple was cancelled or failed. Closing…"
        : "Sign in with Apple complete. Closing…"
    );
    // Slight delay so the BroadcastChannel/postMessage queue drains
    // before we close.  If close is denied by the browser (COOP-
    // opened windows can normally still close themselves, but be
    // defensive), the opener's `signIn()` will still resolve on the
    // payload it already received.
    const t = setTimeout(() => {
      try {
        window.close();
      } catch {
        /* not owner — ignore */
      }
    }, 400);
    return () => clearTimeout(t);
  }, [searchParams, navigate]);

  return (
    <div
      className="min-h-dvh w-full flex flex-col items-center justify-center bg-black text-white px-6"
      data-testid="apple-callback-screen"
    >
      <Seo
        title="Signing in with Apple — BrontoY"
        description="Completing your Sign in with Apple request."
        canonicalPath="/auth/apple/callback"
        noindex
      />
      <div className="flex flex-col items-center gap-4">
        <div
          className="w-10 h-10 rounded-full border-2 border-white/20 border-t-white animate-spin"
          data-testid="apple-callback-spinner"
        />
        <p className="text-white/80 text-[15px] text-center max-w-[360px]" data-testid="apple-callback-status">
          {status}
        </p>
        {!inPopup && (
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-2 h-11 px-6 rounded-full bg-white text-black font-bold text-[14px]"
            data-testid="apple-callback-back-btn"
          >
            Back to Log In
          </button>
        )}
      </div>
    </div>
  );
}
